import { forwardRef } from 'react';

import { Checkbox as BaseCheckbox } from '@base-ui/react/checkbox';

import { CheckBoldIcon } from '../Icon';
import { cn } from '../utils';
import {
  checkboxBoxRecipe,
  checkboxControlRecipe,
  checkboxGlyphRecipe,
  checkboxLabelRecipe,
  checkboxRootRecipe,
} from './Checkbox.recipe';

import type { CheckboxProps } from './types';

/**
 * Design System 2.0 checkbox.
 *
 * Ported from Figma node 585:57 (file 5aSkE0U4TeWbuIZDvccHjc), tracking the 2026-09-15 rework. The
 * set's four variant axes are `State` (Default/Hover/Focus), `isChecked`, `isIndeterminate` and
 * `isDisabled`; of those only the last three are real inputs, because `State` is observed from the
 * DOM rather than passed — Base UI already tracks it. Its two non-variant component properties map
 * straight onto props: the `Label` text property is `label`, and the `Show Label` boolean is `label`
 * being undefined.
 *
 * Theme is not a prop. Colours resolve from the nearest `.dark` ancestor through the token layer, so
 * the same markup is correct in both themes.
 *
 * The wrapper is a real `<label>`, which is Base UI's documented shape for this component: the
 * hidden `<input>` lives inside `Checkbox.Root`, so wrapping both it and the text makes the text a
 * click target and gives the control its accessible name with no `id` plumbing. `Checkbox.Root`
 * itself renders a focusable `<span role="checkbox">` — the visible box is a child of it, since the
 * focus ring overflows the hit area and needs to hang off the box's own edge.
 *
 * Geometry, the state machine, the focus ring and why hover lives on the wrapper are all in
 * `Checkbox.recipe`.
 */
export const Checkbox = forwardRef<HTMLElement, CheckboxProps>(
  ({ label, className, rootClassName, labelClassName, disabled, ...rest }, ref) => (
    <label className={cn(checkboxRootRecipe({ disabled, unlabelled: label === undefined }), rootClassName)}>
      <BaseCheckbox.Root ref={ref} className={cn(checkboxControlRecipe(), className)} disabled={disabled} {...rest}>
        {/* `data-part` names the box so a test can assert on it without depending on a styling
            class, as `TextInput` does for its bordered box. */}
        <span className={checkboxBoxRecipe()} data-part="box" />

        {/* Keep both glyphs mounted so CSS follows mixed states inherited from CheckboxGroup.
            Figma scales the 16px selection icon to 14px at (5, 5) in the 24px control frame. */}
        <BaseCheckbox.Indicator className={checkboxGlyphRecipe()}>
          <svg aria-hidden="true" fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <CheckBoldIcon className="group-data-[indeterminate]/cb:hidden" data-part="tick" size={14} x={5} y={5} />
            <path
              className="hidden group-data-[indeterminate]/cb:block"
              d="M8 12H16"
              data-part="dash"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
            />
          </svg>
        </BaseCheckbox.Indicator>
      </BaseCheckbox.Root>

      {label !== undefined && <span className={cn(checkboxLabelRecipe({ disabled }), labelClassName)}>{label}</span>}
    </label>
  )
);
